import {
  Avatar,
  AvatarFallback,
  Button,
  ButtonProps,
  Icon,
  cn,
} from '@sr/ui-library';
import { forwardRef } from 'react';

/**
 * Example usage:
 *
 * ```tsx
 * <AppDropdownButton>
 *   <AppDropdownButton.Avatar>
 *     {userInitials(user.fullName)}
 *   </AppDropdownButton.Avatar>
 *   <AppDropdownButton.Text>
 *     {user.fullName}
 *   </AppDropdownButton.Text>
 * </AppDropdownButton>
 *
 * // Used with triggers
 * <DropdownMenuTrigger asChild>
 *   <AppDropdownButton>
 *     // Same as above
 *   </AppDropdownButton>
 * </DropdownMenuTrigger>
 * ```
 */
// eslint-disable-next-line react/display-name -- lint-backlog: anonymous forwardRef/memo component; add a displayName in a follow-up
const AppDropdownButtonBase = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, ...props }, ref) => (
    <Button
      className={cn('!h-auto gap-3 whitespace-normal p-2', className)}
      ref={ref}
      sectionRight={
        <Icon className="text-black" icon="chevronUpDown" weight="bold" />
      }
      size="content"
      variant="ghost"
      {...props}
    />
  ),
);

const AppDropdownAvatar = ({ children }: React.PropsWithChildren) => (
  <Avatar variant="square">
    <AvatarFallback className="bg-black text-sm text-white">
      {children}
    </AvatarFallback>
  </Avatar>
);

const AppDropdownButtonText = ({ children }: React.PropsWithChildren) => (
  <div className="grow text-left text-sm font-medium text-black">
    {children}
  </div>
);

export const AppDropdownButton = Object.assign(AppDropdownButtonBase, {
  Avatar: AppDropdownAvatar,
  Text: AppDropdownButtonText,
});
